import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import React, { useEffect } from 'react';
import { Button } from './components/ui/button'; // Import Button component

// Import modules
const HomePage = React.lazy(() => import("./modules/home"));
const AboutPage = React.lazy(() => import("./modules/about"));
const AdHocServicesPage = React.lazy(() => import("./modules/services/ad-hoc"));
const QuickWinsPage = React.lazy(() => import("./modules/services/quick-wins"));
const PMOTransformationPage = React.lazy(() => import("./modules/services/pmo-transformation"));
const PrecisionPMOPage = React.lazy(() => import("./modules/services/precision-pmo"));
const FractionalPMOPage = React.lazy(() => import("./modules/services/fractional-pmo"));
const CoachingPage = React.lazy(() => import("./modules/services/project-leadership-coaching"));
const AssessmentPage = React.lazy(() => import("./modules/assessment"));
const ContactPage = React.lazy(() => import("./modules/contact"));
const PrivacyPolicy = React.lazy(() => import("./modules/privacy"));
const TermsAndConditions = React.lazy(() => import("./modules/terms"));
// Blog page removed - now linking directly to WordPress
const NotFound = React.lazy(() => import("./modules/notFound"));
// Test page removed for production

// Import shared components (ensure paths are correct)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MainLayout from './components/MainLayout';
import HomeLayout from './components/HomeLayout'; // Import the home layout component without footer

const queryClient = new QueryClient();

// Component to handle scroll restoration
const ScrollRestoration = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollRestoration />
        <Routes>
          {/* Homepage route with no footer */}
          <Route element={<HomeLayout />}>
            <Route path="/" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><HomePage /></React.Suspense>} />
          </Route>

          {/* Other routes using the main layout with footer */}
          <Route element={<MainLayout />}>
            <Route path="/about" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><AboutPage /></React.Suspense>} />
            <Route path="/services" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><AdHocServicesPage /></React.Suspense>} />
            <Route path="/services/quick-wins" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><QuickWinsPage /></React.Suspense>} />
            <Route path="/services/pmo-transformation" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><PMOTransformationPage /></React.Suspense>} />
            <Route path="/services/precision-pmo" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><PrecisionPMOPage /></React.Suspense>} />
            <Route path="/services/fractional-pmo" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><FractionalPMOPage /></React.Suspense>} />
            <Route path="/services/project-leadership-coaching" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><CoachingPage /></React.Suspense>} />
            <Route path="/assessment/*" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><AssessmentPage /></React.Suspense>} />
            <Route path="/contact" element={<React.Suspense fallback={<div className='p-4'>Loading Contact...</div>}><ContactPage /></React.Suspense>} />
            <Route path="/privacy" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><PrivacyPolicy /></React.Suspense>} />
            <Route path="/terms" element={<React.Suspense fallback={<div className='p-4'>Loading...</div>}><TermsAndConditions /></React.Suspense>} />
            {/* Blog routes removed - now linking directly to WordPress */}
          </Route>

          {/* Routes without the main layout (or with a different layout) */}
          {/* Test page removed for production */}
          <Route path="*" element={<React.Suspense fallback={null}><NotFound /></React.Suspense>} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
